import React, {useRef} from 'react';
import {useSwipeable} from 'react-swipeable';
import {
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
  CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
} from '../ContentPopper/utils/contentPopperAnimationDurations';

export const CLICK_OUTSIDE_LAYOUT_SWIPABLE = false;

export type DraggableBottomsheetProps = {
  draggedPercentageToClose: number;
  onClose: VoidFunction;
  dialogRef: React.RefObject<HTMLElement | null>;
  headerRef: React.MutableRefObject<HTMLElement | null>;
};

export const useDraggableBottomsheet = ({
  onClose,
  draggedPercentageToClose,
  dialogRef,
  headerRef,
}: DraggableBottomsheetProps) => {
  const deltaYCorrection = React.useRef(-1);
  const deltaYPositionAfterSwiped = React.useRef(-1);
  const canSwipeOut = useRef(false);

  const {ref: swipableRef} = useSwipeable({
    preventDefaultTouchmoveEvent: false,
    onSwipeStart: () => {
      canSwipeOut.current = false;
    },
    onSwipedDown: () => {
      const shouldClose = getShouldClose({
        canSwipeOut,
        dialogRef,
        deltaYPositionAfterSwiped,
        draggedPercentageToClose,
      });

      if (shouldClose) {
        dialogRef.current?.style.setProperty(
          'transform',
          `translateY(${dialogRef.current.clientHeight}px)`,
        );
        dialogRef.current?.style.setProperty('opacity', '0');
        onClose();
      }
      deltaYPositionAfterSwiped.current = -1;
    },
    onSwiping: ({deltaY, event, dir}) => {
      event.stopPropagation();

      if (dir !== 'Down') {
        return null;
      }

      if (!canSwipeOut.current) {
        canSwipeOut.current = true;
      }

      if (deltaYCorrection.current === -1) {
        /**
         * Added a correction since delta can be different from the moment whe the user starts swiping
         * to the moment when the Flyout can be swiped down. This correction is only set once, when the
         * Flyout can be swiped down
         * */
        deltaYCorrection.current = deltaY;
      }

      const translateYPosition = deltaY - deltaYCorrection.current;

      dialogRef.current?.style.setProperty('transition', 'none');
      dialogRef.current?.style.setProperty(
        'transform',
        `translateY(max(${translateYPosition}px, 0px))`,
      );
    },
    onSwiped: ({deltaY}) => {
      if (deltaY > 0) {
        deltaYPositionAfterSwiped.current = deltaY - deltaYCorrection.current;
      }

      const shouldClose = getShouldClose({
        canSwipeOut,
        dialogRef,
        deltaYPositionAfterSwiped,
        draggedPercentageToClose,
      });

      const transitionDuration = shouldClose
        ? CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT
        : CONTENT_POPPER_ANIMATION_DURATION_MOUNT;

      const transitionTime = `${transitionDuration}ms ease-in-out`;

      dialogRef.current?.style.setProperty(
        'transition',
        `transform ${transitionTime}, opacity ${transitionTime}`,
      );

      if (!shouldClose) {
        dialogRef.current?.style.setProperty('transform', 'translateY(0)');
      }
      deltaYCorrection.current = -1;
    },
  });

  const passRefThrough = (el: HTMLElement | null) => {
    swipableRef(el);
    headerRef.current = el;
  };

  return {passRefThrough};
};

const getShouldClose = ({
  dialogRef,
  deltaYPositionAfterSwiped,
  canSwipeOut,
  draggedPercentageToClose,
}: {
  dialogRef: React.RefObject<HTMLElement | null>;
  deltaYPositionAfterSwiped: React.RefObject<number | null>;
  canSwipeOut: React.RefObject<boolean | null>;
  draggedPercentageToClose: DraggableBottomsheetProps['draggedPercentageToClose'];
}) => {
  const deltaY = deltaYPositionAfterSwiped.current ?? -1;

  return (
    canSwipeOut.current &&
    dialogRef.current &&
    deltaY > dialogRef.current.clientHeight * draggedPercentageToClose
  );
};
